// ---------------------------------------------------------------------------
// API client.
//
// VITE_API_BASE_URL is baked at build time. There is no runtime environment
// switch by design: one Static Web App deployment talks to exactly one API,
// which in turn talks to exactly one database. See docs/ARCHITECTURE.md.
// ---------------------------------------------------------------------------

const BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request(method, path, body) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      // Sends the httpOnly session cookie. Requires the API to allowlist this
      // exact origin in CORS_ALLOWED_ORIGINS.
      credentials: 'include',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Cannot reach the API. Check that it is running.');
  }

  if (res.status === 204) return null;

  let payload = null;
  const text = await res.text();
  if (text !== '') {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new ApiError(res.status, 'BAD_RESPONSE', `Unexpected response from server (${res.status})`);
    }
  }

  if (!res.ok) {
    throw new ApiError(
      res.status,
      payload?.error ?? 'ERROR',
      payload?.message ?? `Request failed (${res.status})`,
      payload?.details,
    );
  }

  return payload;
}

const qs = (params) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s === '' ? '' : `?${s}`;
};

export const api = {
  // --- auth ---
  login: (email, password) => request('POST', '/api/auth/login', { email, password }),
  logout: () => request('POST', '/api/auth/logout'),
  me: () => request('GET', '/api/auth/me'),
  meta: () => request('GET', '/api/meta'),

  // --- dashboard / projects (read-only) ---
  dashboard: () => request('GET', '/api/dashboard'),
  listProjects: (opts = {}) => request('GET', `/api/projects${qs(opts)}`),
  projectStats: () => request('GET', '/api/projects/stats'),

  // --- organisations ---
  listOrgs: (opts = {}) => request('GET', `/api/orgs${qs(opts)}`),
  orgStats: () => request('GET', '/api/orgs/stats'),
  getOrg: (orgId) => request('GET', `/api/orgs/${orgId}`),
  listOrgActivity: (orgId, opts = {}) => request('GET', `/api/orgs/${orgId}/activity${qs(opts)}`),
  createOrg: (payload) => request('POST', '/api/orgs', payload),
  deleteOrg: (orgId, confirmName) => request('DELETE', `/api/orgs/${orgId}`, { confirmName }),
  restoreOrg: (orgId) => request('POST', `/api/orgs/${orgId}/restore`),
  listExpiringTrials: (opts = {}) => request('GET', `/api/orgs/trials/expiring${qs(opts)}`),
  updateTrialDates: (orgId, payload) => request('PATCH', `/api/orgs/${orgId}/trial-dates`, payload),
  updatePlanWindow: (orgId, payload) => request('PATCH', `/api/orgs/${orgId}/plan-window`, payload),
  setOrgStatus: (orgId, isActive) => request('PATCH', `/api/orgs/${orgId}/status`, { isActive }),
  setPlan: (orgId, plan) => request('PATCH', `/api/orgs/${orgId}/plan`, { plan }),
  setMaxProjects: (orgId, maxProjects) =>
    request('PATCH', `/api/orgs/${orgId}/max-projects`, { maxProjects }),
  setMaxUsers: (orgId, maxUsers) =>
    request('PATCH', `/api/orgs/${orgId}/max-users`, { maxUsers }),
  setRevalidationsRemaining: (orgId, revalidationsRemaining) =>
    request('PATCH', `/api/orgs/${orgId}/max-revalidations`, { revalidationsRemaining }),
  setCocoPromptsLeft: (orgId, cocoPromptsLeft) =>
    request('PATCH', `/api/orgs/${orgId}/coco-prompts-left`, { cocoPromptsLeft }),
  listOrgProjects: (orgId, opts = {}) => request('GET', `/api/orgs/${orgId}/projects${qs(opts)}`),

  // --- users ---
  listUsers: (orgId, opts = {}) => request('GET', `/api/orgs/${orgId}/users${qs(opts)}`),
  listAllUsers: (opts = {}) => request('GET', `/api/users${qs(opts)}`),
  getUser: (userId) => request('GET', `/api/users/${userId}`),
  listUserActivity: (userId, opts = {}) => request('GET', `/api/users/${userId}/activity${qs(opts)}`),
  addUser: (orgId, payload) => request('POST', `/api/orgs/${orgId}/users`, payload),
  setRole: (orgId, userId, role) =>
    request('PATCH', `/api/memberships/${orgId}/${userId}/role`, { role }),
  setMembershipStatus: (orgId, userId, isActive) =>
    request('PATCH', `/api/memberships/${orgId}/${userId}/status`, { isActive }),
  removeMember: (orgId, userId) => request('DELETE', `/api/memberships/${orgId}/${userId}`),
  resendInvite: (orgId, userId) =>
    request('POST', `/api/memberships/${orgId}/${userId}/resend-invite`),
  deleteUser: (userId, confirmEmail) => request('DELETE', `/api/users/${userId}`, { confirmEmail }),
  restoreUser: (userId, confirmEmail) =>
    request('POST', `/api/users/${userId}/restore`, { confirmEmail }),
  setUserAccountStatus: (userId, isActive) =>
    request('PATCH', `/api/users/${userId}/status`, { isActive }),
};

export const ORG_ROLES = ['OWNER', 'ADMIN', 'PROJECT_PLANNER', 'SPECIALIST_CONTRIBUTOR', 'PMO'];
